import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import BlogCard from './BlogCard';
import BlogCategories from './BlogCategories';
import BlogSearch from './BlogSearch';
import SEO from '../SEO';

interface BlogPost {
    _id: string;
    title: string;
    excerpt: string;
    featuredImage?: string;
    author: {
        name: string;
        avatar?: string;
    };
    category: string;
    tags: string[];
    readTime: number;
    views: number;
    likes: number;
    publishedAt: string;
    slug: string;
}

interface PaginationData {
    page: number;
    limit: number;
    total: number;
    pages: number;
}

export default function BlogList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [posts, setPosts] = useState<BlogPost[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState<PaginationData>({
        page: 1,
        limit: 12,
        total: 0,
        pages: 0
    });

    // Get current filters from URL
    const currentPage = parseInt(searchParams.get('page') || '1');
    const currentCategory = searchParams.get('category') || '';
    const currentSearch = searchParams.get('search') || '';
    const currentTag = searchParams.get('tag') || '';
    const sortBy = searchParams.get('sortBy') || 'publishedAt';
    const sortOrder = searchParams.get('sortOrder') || 'desc';

    // Fetch blog posts
    const fetchPosts = async () => {
        try {
            setLoading(true);
            setError(null);

            const params = new URLSearchParams();
            params.set('page', currentPage.toString());
            params.set('limit', '12');
            if (currentCategory) params.set('category', currentCategory);
            if (currentSearch) params.set('search', currentSearch);
            if (currentTag) params.set('tag', currentTag);
            params.set('sortBy', sortBy);
            params.set('sortOrder', sortOrder);

            const response = await fetch(`/api/blog/posts?${params}`);
            const data = await response.json();

            if (data.success) {
                setPosts(data.data);
                setPagination(data.pagination);
            } else {
                setError('Failed to load blog posts');
            }
        } catch (err) {
            setError('Failed to load blog posts');
            console.error('Error fetching posts:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPosts();
    }, [currentPage, currentCategory, currentSearch, currentTag, sortBy, sortOrder]);

    // Handle filter changes
    const handleSearch = (query: string) => {
        const newParams = new URLSearchParams(searchParams);
        if (query) {
            newParams.set('search', query);
        } else {
            newParams.delete('search');
        }
        newParams.set('page', '1');
        setSearchParams(newParams);
    };

    const handleCategoryChange = (category: string) => {
        const newParams = new URLSearchParams(searchParams);
        if (category) {
            newParams.set('category', category);
        } else {
            newParams.delete('category');
        }
        newParams.set('page', '1');
        setSearchParams(newParams);
    };

    const handleTagClick = (tag: string) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('tag', tag);
        newParams.set('page', '1');
        setSearchParams(newParams);
    };

    const handleSortChange = (newSortBy: string, newSortOrder: string) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('sortBy', newSortBy);
        newParams.set('sortOrder', newSortOrder);
        newParams.set('page', '1');
        setSearchParams(newParams);
    };

    const handlePageChange = (page: number) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('page', page.toString());
        setSearchParams(newParams);
    };

    const clearFilters = () => {
        setSearchParams({});
    };

    const activeFiltersCount = [currentCategory, currentSearch, currentTag].filter(Boolean).length;

    return (
        <div className="min-h-screen bg-gray-50">
                  <SEO 
        page="blog"
        title="English Learning Blog - Tips, Grammar & Success Stories"
        description="Discover expert English learning tips, grammar guides, vocabulary building techniques, and inspiring success stories from our AI-powered learning platform."
      />

            {/* Hero Section */}
            <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    <div className="text-center">
                        <h1 className="text-4xl md:text-5xl font-bold mb-6">
                            📚 English Learning Blog
                        </h1>
                        <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto">
                            Expert tips, grammar guides, vocabulary building, and inspiring success stories
                            to accelerate your English learning journey
                        </p>
                        <div className="flex flex-wrap justify-center gap-4 text-sm">
                            <div className="bg-white/20 px-4 py-2 rounded-full backdrop-blur-sm">
                                📖 Grammar Mastery
                            </div>
                            <div className="bg-white/20 px-4 py-2 rounded-full backdrop-blur-sm">
                                🔤 Vocabulary Building
                            </div>
                            <div className="bg-white/20 px-4 py-2 rounded-full backdrop-blur-sm">
                                🤖 AI Learning Tips
                            </div>
                            <div className="bg-white/20 px-4 py-2 rounded-full backdrop-blur-sm">
                                🏆 Success Stories
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Sidebar */}
                    <div className="lg:w-1/4">
                        <div className="sticky top-8 space-y-6">
                            {/* Search */}
                            <BlogSearch
                                value={currentSearch}
                                onSearch={handleSearch}
                                placeholder="Search blog posts..."
                            />

                            {/* Categories */}
                            <BlogCategories
                                selectedCategory={currentCategory}
                                onCategoryChange={handleCategoryChange}
                            />

                            {/* Sort Options */}
                            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                                <h3 className="text-lg font-semibold text-gray-800 mb-4">Sort By</h3>
                                <select
                                    value={`${sortBy}-${sortOrder}`}
                                    onChange={(e) => {
                                        const [newSortBy, newSortOrder] = e.target.value.split('-');
                                        handleSortChange(newSortBy, newSortOrder);
                                    }}
                                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="publishedAt-desc">Latest First</option>
                                    <option value="publishedAt-asc">Oldest First</option>
                                    <option value="views-desc">Most Viewed</option>
                                    <option value="likes-desc">Most Liked</option>
                                    <option value="readTime-asc">Quick Reads</option>
                                    <option value="readTime-desc">Long Reads</option>
                                </select>
                            </div>

                            {/* Active Filters */}
                            {activeFiltersCount > 0 && (
                                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Active Filters</h3>
                                    <div className="space-y-2">
                                        {currentCategory && (
                                            <div className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded-lg">
                                                <span className="text-sm text-blue-800">Category: {currentCategory}</span>
                                                <button
                                                    onClick={() => handleCategoryChange('')}
                                                    className="text-blue-600 hover:text-blue-800"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        )}
                                        {currentSearch && (
                                            <div className="flex items-center justify-between bg-green-50 px-3 py-2 rounded-lg">
                                                <span className="text-sm text-green-800">Search: "{currentSearch}"</span>
                                                <button
                                                    onClick={() => handleSearch('')}
                                                    className="text-green-600 hover:text-green-800"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        )}
                                        {currentTag && (
                                            <div className="flex items-center justify-between bg-purple-50 px-3 py-2 rounded-lg">
                                                <span className="text-sm text-purple-800">Tag: #{currentTag}</span>
                                                <button
                                                    onClick={() => handleTagClick('')}
                                                    className="text-purple-600 hover:text-purple-800"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={clearFilters}
                                        className="w-full mt-4 px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        Clear All Filters
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="lg:w-3/4">
                        {/* Results Header */}
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-800">
                                    {currentSearch ? `Search Results for "${currentSearch}"` :
                                        currentCategory ? `${currentCategory.replace('-', ' ').toUpperCase()} Posts` :
                                            currentTag ? `Posts tagged with #${currentTag}` :
                                                'All Blog Posts'}
                                </h2>
                                <p className="text-gray-600 mt-1">
                                    {pagination.total} {pagination.total === 1 ? 'post' : 'posts'} found
                                </p>
                            </div>
                        </div>

                        {/* Loading State */}
                        {loading && (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="bg-white rounded-2xl shadow-lg overflow-hidden animate-pulse">
                                        <div className="h-48 bg-gray-200"></div>
                                        <div className="p-6">
                                            <div className="h-4 bg-gray-200 rounded mb-2"></div>
                                            <div className="h-4 bg-gray-200 rounded mb-4 w-3/4"></div>
                                            <div className="h-3 bg-gray-200 rounded mb-2"></div>
                                            <div className="h-3 bg-gray-200 rounded mb-2"></div>
                                            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Error State */}
                        {error && (
                            <div className="text-center py-12">
                                <div className="bg-red-50 border border-red-200 rounded-xl p-8 max-w-md mx-auto">
                                    <svg className="w-12 h-12 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <h3 className="text-lg font-semibold text-red-800 mb-2">Failed to Load Posts</h3>
                                    <p className="text-red-600 mb-4">{error}</p>
                                    <button
                                        onClick={fetchPosts}
                                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                    >
                                        Try Again
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Posts Grid */}
                        {!loading && !error && posts.length > 0 && (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-12">
                                    {posts.map((post) => (
                                        <BlogCard
                                            key={post._id}
                                            post={post}
                                            className="hover:scale-[1.02] transition-transform duration-200"
                                        />
                                    ))}
                                </div>

                                {/* Pagination */}
                                {pagination.pages > 1 && (
                                    <div className="flex items-center justify-center space-x-2">
                                        {/* Previous Button */}
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Previous
                                        </button>

                                        {/* Page Numbers */}
                                        {[...Array(pagination.pages)].map((_, i) => {
                                            const page = i + 1;
                                            const isCurrentPage = page === currentPage;

                                            // Show first page, last page, current page, and pages around current
                                            const showPage = page === 1 ||
                                                page === pagination.pages ||
                                                Math.abs(page - currentPage) <= 1;

                                            if (!showPage) {
                                                // Show ellipsis for gaps
                                                if (page === 2 && currentPage > 4) {
                                                    return <span key={page} className="px-3 py-2 text-gray-500">...</span>;
                                                }
                                                if (page === pagination.pages - 1 && currentPage < pagination.pages - 3) {
                                                    return <span key={page} className="px-3 py-2 text-gray-500">...</span>;
                                                }
                                                return null;
                                            }

                                            return (
                                                <button
                                                    key={page}
                                                    onClick={() => handlePageChange(page)}
                                                    className={`px-4 py-2 text-sm font-medium rounded-lg ${isCurrentPage
                                                            ? 'bg-blue-600 text-white'
                                                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                                        }`}
                                                >
                                                    {page}
                                                </button>
                                            );
                                        })}

                                        {/* Next Button */}
                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === pagination.pages}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Empty State */}
                        {!loading && !error && posts.length === 0 && (
                            <div className="text-center py-12">
                                <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 max-w-md mx-auto">
                                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                    </svg>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-2">No Posts Found</h3>
                                    <p className="text-gray-600 mb-4">
                                        {activeFiltersCount > 0
                                            ? "Try adjusting your filters or search terms."
                                            : "No blog posts available at the moment."
                                        }
                                    </p>
                                    {activeFiltersCount > 0 && (
                                        <button
                                            onClick={clearFilters}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                        >
                                            Clear Filters
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
} 